import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Plus, Pencil, Trash2, X, Check, ShieldCheck, Briefcase, ListChecks, MapPin, LocateFixed, Wallet } from "lucide-react";
import { PERM_GROUPS } from "@/pages/admin/AdminEmployeeForm";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const LOC_PIN = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:9999px;background:#C4738A;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.4);cursor:grab"></div>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
});

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const ROUTES = [["/admin", "Dashboard Admin"], ["/pos", "POS Kasir"], ["/produksi", "Portal Karyawan"]];
const blankRole = { label: "", default_route: "/produksi", permissions: [] };

export default function AdminMasterData() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(["aspek", "statuses", "lokasi", "payroll"].includes(params.get("tab")) ? params.get("tab") : "roles");
  return (
    <div data-testid="master-page" className="space-y-5 pb-10">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Master Data</h1>
        <p className="text-xs text-slate-400">Kelola jabatan/role, status kepegawaian & aspek penilaian — perubahan langsung dipakai wizard karyawan dan modul penilaian.</p>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[{ id: "roles", label: "Jabatan & Role", Icon: ShieldCheck }, { id: "statuses", label: "Status Kepegawaian", Icon: Briefcase }, { id: "aspek", label: "Aspek Penilaian", Icon: ListChecks }, { id: "lokasi", label: "Lokasi Kerja", Icon: MapPin }, { id: "payroll", label: "Aturan Payroll", Icon: Wallet }].map((t) => (
          <button key={t.id} data-testid={`master-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "roles" ? <RolesTab /> : tab === "statuses" ? <StatusesTab /> : tab === "aspek" ? <AspectsTab /> : tab === "payroll" ? <PayrollRulesTab /> : <LokasiTab />}
    </div>
  );
}

function AspectsTab() {
  const [items, setItems] = useState(null);
  const [newName, setNewName] = useState("");
  const [newWeight, setNewWeight] = useState(10);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editWeight, setEditWeight] = useState(10);
  const load = () => api.get("/admin/appraisal-aspects").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const total = (items || []).reduce((s, a) => s + a.weight, 0);

  const add = async () => {
    if (newName.trim().length < 2) return toast.error("Nama aspek minimal 2 karakter");
    try { await api.post("/admin/appraisal-aspects", { name: newName.trim(), weight: parseInt(newWeight) || 10 }); toast.success("Aspek ditambahkan"); setNewName(""); setNewWeight(10); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah aspek"); }
  };
  const save = async (a) => {
    if (editName.trim().length < 2) return toast.error("Nama aspek minimal 2 karakter");
    try { await api.put(`/admin/appraisal-aspects/${a.id}`, { name: editName.trim(), weight: parseInt(editWeight) || a.weight }); toast.success("Aspek diperbarui"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };
  const del = async (a) => {
    if (!window.confirm(`Hapus aspek "${a.name}"? Riwayat penilaian lama tidak berubah.`)) return;
    try { await api.delete(`/admin/appraisal-aspects/${a.id}`); toast.success("Aspek dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-2 flex-wrap">
        <input data-testid="maspect-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Aspek baru, mis. Inisiatif..." className={`${inp} w-64`} />
        <div className="flex items-center gap-1">
          <input data-testid="maspect-new-weight" type="number" min="1" max="100" value={newWeight} onChange={(e) => setNewWeight(e.target.value)} className={`${inp} w-20 text-center`} />
          <span className="text-[11px] text-slate-400">%</span>
        </div>
        <button data-testid="maspect-add-btn" onClick={add} className="h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"><Plus className="w-3.5 h-3.5" /> Tambah Aspek</button>
        <span data-testid="maspect-total" className={`ml-auto text-[11px] font-bold ${total === 100 ? "text-emerald-600" : "text-amber-500"}`}>Total bobot: {total}%{total !== 100 && " — idealnya 100%"}</span>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Aspek Penilaian</th>
              <th className="text-center px-3 py-2.5 font-semibold w-24">Bobot</th>
              <th className="px-3 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={3} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {(items || []).map((a) => (
              <tr key={a.id} data-testid={`maspect-row-${a.id}`}>
                <td className="px-4 py-2.5">
                  {editing === a.id ? (
                    <input data-testid={`maspect-edit-name-${a.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus className={`${inp} w-72`} />
                  ) : (
                    <span className="text-xs font-semibold text-slate-800">{a.name}
                      {a.auto === "attendance" && <span className="ml-2 text-[9px] font-bold bg-emerald-50 text-emerald-600 rounded-full px-2 py-0.5">Otomatis (absensi)</span>}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {editing === a.id ? (
                    <input data-testid={`maspect-edit-weight-${a.id}`} type="number" min="1" max="100" value={editWeight} onChange={(e) => setEditWeight(e.target.value)} className={`${inp} w-20 text-center mx-auto`} />
                  ) : <span className="text-[11px] font-bold text-slate-500">{a.weight}%</span>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    {editing === a.id ? (
                      <>
                        <button data-testid={`maspect-save-${a.id}`} onClick={() => save(a)} className="w-8 h-8 rounded-lg bg-[#C4738A] text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                      </>
                    ) : (
                      <>
                        <button data-testid={`maspect-edit-${a.id}`} onClick={() => { setEditing(a.id); setEditName(a.name); setEditWeight(a.weight); }} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`maspect-del-${a.id}`} onClick={() => del(a)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400">Aspek "Kehadiran" dinilai otomatis dari data absensi (toleransi telat 10 menit). Aspek lain dinilai manual saat membuat penilaian. Menghapus aspek tidak mengubah riwayat penilaian lama.</p>
    </div>
  );
}

function RolesTab() {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const { paged: pagedRoles, pager: rolePager } = usePager(items || []);
  const [form, setForm] = useState(blankRole);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/master/roles").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const startAdd = () => { setEditing(null); setForm(blankRole); setShowForm(true); };
  const startEdit = (r) => { setEditing(r.id); setForm({ label: r.label, default_route: r.default_route || "/produksi", permissions: r.permissions || [] }); setShowForm(true); };
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const save = async () => {
    if (form.label.trim().length < 2) return toast.error("Nama role wajib diisi (min. 2 karakter)");
    setBusy(true);
    try {
      if (editing) { await api.put(`/admin/master/roles/${editing}`, form); toast.success("Role diperbarui — berlaku langsung ke semua karyawan dengan role ini"); }
      else { await api.post("/admin/master/roles", form); toast.success("Role baru ditambahkan"); }
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan role"); }
    finally { setBusy(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Hapus role "${r.label}"?${r.user_count ? ` Masih dipakai ${r.user_count} karyawan.` : ""}`)) return;
    try { await api.delete(`/admin/master/roles/${r.id}`); toast.success("Role dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-slate-400">{items?.length ?? 0} role terdaftar</span>
        <button data-testid="role-add-btn" onClick={startAdd} className="ml-auto h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"><Plus className="w-3.5 h-3.5" /> Tambah Role</button>
      </div>

      {showForm && (
        <div data-testid="role-form" className="bg-white rounded-2xl border border-[#C4738A]/30 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">{editing ? `Ubah Role: ${editing}` : "Role Baru"}</h3>
            <button onClick={() => setShowForm(false)} className="w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Nama Role *</label>
              <input data-testid="role-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="mis. Admin Live Streaming" className={`${inp} w-full`} />
              {!editing && form.label.trim() && <p className="text-[10px] text-slate-400 mt-1">ID otomatis: <span className="font-mono">{form.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")}</span></p>}</div>
            <div><label className="text-[11px] font-semibold text-slate-500 block mb-1">Halaman Tujuan Setelah Login</label>
              <select data-testid="role-route" value={form.default_route} onChange={(e) => setForm((f) => ({ ...f, default_route: e.target.value }))} className={`${inp} w-full`}>
                {ROUTES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 block mb-1.5">Permission Bawaan Role</label>
            <div className="border border-slate-100 rounded-xl divide-y divide-slate-50 max-h-64 overflow-y-auto">
              {PERM_GROUPS.map((g) => (
                <div key={g.label} className="px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 w-28 flex-shrink-0">{g.label}</span>
                  {g.perms.map(([p, l]) => (
                    <label key={p} className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                      <input type="checkbox" data-testid={`role-perm-${p.replace(".", "-")}`} checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#C4738A]" />
                      {l}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
            <button data-testid="role-save" onClick={save} disabled={busy} className="h-9 px-5 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold disabled:opacity-50">{editing ? "Simpan Perubahan" : "Tambah Role"}</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Role</th>
              <th className="text-left px-3 py-2.5 font-semibold">ID</th>
              <th className="text-left px-3 py-2.5 font-semibold">Halaman Login</th>
              <th className="text-center px-3 py-2.5 font-semibold w-24">Permission</th>
              <th className="text-center px-3 py-2.5 font-semibold w-24">Karyawan</th>
              <th className="px-3 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={6} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {pagedRoles.map((r) => (
              <tr key={r.id} data-testid={`role-row-${r.id}`}>
                <td className="px-4 py-3">
                  <span className="text-xs font-semibold text-slate-800">{r.label}</span>
                  {r.is_system && <span className="ml-2 text-[9px] font-bold bg-slate-100 text-slate-400 rounded-full px-2 py-0.5">bawaan</span>}
                </td>
                <td className="px-3 py-3 text-[10px] font-mono text-slate-400">{r.id}</td>
                <td className="px-3 py-3 text-[11px] text-slate-500">{ROUTES.find(([v]) => v === r.default_route)?.[1] || r.default_route}</td>
                <td className="px-3 py-3 text-center"><span className="text-[10px] font-bold bg-rose-50 text-[#C4738A] rounded-full px-2 py-0.5">{r.id === "owner" ? "Semua" : (r.permissions || []).length}</span></td>
                <td className="px-3 py-3 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.user_count > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{r.user_count}</span></td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`role-edit-${r.id}`} onClick={() => startEdit(r)} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`role-delete-${r.id}`} onClick={() => del(r)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager {...rolePager} testid="role-pager" />
      <p className="text-[10px] text-slate-400">Role yang sedang dipakai karyawan tidak bisa dihapus — pindahkan dulu karyawannya. Role Owner selalu memiliki akses penuh.</p>
    </div>
  );
}

function StatusesTab() {
  const [items, setItems] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const load = () => api.get("/admin/master/emp-statuses").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (newLabel.trim().length < 2) return toast.error("Nama status minimal 2 karakter");
    try { await api.post("/admin/master/emp-statuses", { label: newLabel.trim() }); toast.success("Status ditambahkan"); setNewLabel(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah status"); }
  };
  const rename = async (s) => {
    if (editLabel.trim().length < 2) return toast.error("Nama status minimal 2 karakter");
    try { await api.put(`/admin/master/emp-statuses/${s.id}`, { label: editLabel.trim() }); toast.success("Status diperbarui"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Hapus status "${s.label}"?`)) return;
    try { await api.delete(`/admin/master/emp-statuses/${s.id}`); toast.success("Status dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const list = useMemo(() => items || [], [items]);
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-2 flex-wrap">
        <input data-testid="status-new" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Status baru, mis. Probation / Freelance..." className={`${inp} w-64`} />
        <button data-testid="status-add-btn" onClick={add} className="h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"><Plus className="w-3.5 h-3.5" /> Tambah Status</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Status Kepegawaian</th>
              <th className="text-left px-3 py-2.5 font-semibold">ID</th>
              <th className="text-center px-3 py-2.5 font-semibold w-24">Karyawan</th>
              <th className="px-3 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={4} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {list.map((s) => (
              <tr key={s.id} data-testid={`status-row-${s.id}`}>
                <td className="px-4 py-2.5">
                  {editing === s.id ? (
                    <div className="flex items-center gap-1.5">
                      <input data-testid={`status-edit-input-${s.id}`} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(s)} autoFocus className={`${inp} w-52`} />
                      <button data-testid={`status-edit-save-${s.id}`} onClick={() => rename(s)} className="h-8 px-3 rounded-lg bg-[#C4738A] text-white text-[11px] font-semibold">Simpan</button>
                      <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-800">{s.label}{s.is_system && <span className="ml-2 text-[9px] font-bold bg-slate-100 text-slate-400 rounded-full px-2 py-0.5">bawaan</span>}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[10px] font-mono text-slate-400">{s.id}</td>
                <td className="px-3 py-2.5 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.user_count > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{s.user_count}</span></td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`status-edit-${s.id}`} onClick={() => { setEditing(s.id); setEditLabel(s.label); }} title="Ubah nama" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`status-delete-${s.id}`} onClick={() => del(s)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400">Status yang sedang dipakai karyawan tidak bisa dihapus.</p>
    </div>
  );
}

function LokasiTab() {
  const [items, setItems] = useState(null);
  const blank = { name: "", lat: "", lng: "", radius_m: 150 };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markRef = useRef(null);
  const circRef = useRef(null);
  const load = () => api.get("/admin/work-locations").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { attributionControl: false }).setView([-6.732, 108.5523], 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    map.on("click", (e) => setForm((f) => ({ ...f, lat: e.latlng.lat.toFixed(6), lng: e.latlng.lng.toFixed(6) })));
    mapObj.current = map;
    return () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; markRef.current = null; circRef.current = null; } };
  }, []);

  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    const lat = parseFloat(form.lat), lng = parseFloat(form.lng);
    if (isNaN(lat) || isNaN(lng)) {
      if (markRef.current) { markRef.current.remove(); markRef.current = null; }
      if (circRef.current) { circRef.current.remove(); circRef.current = null; }
      return;
    }
    if (!markRef.current) {
      markRef.current = L.marker([lat, lng], { draggable: true, icon: LOC_PIN }).addTo(map);
      markRef.current.on("dragend", () => {
        const p = markRef.current.getLatLng();
        setForm((f) => ({ ...f, lat: p.lat.toFixed(6), lng: p.lng.toFixed(6) }));
      });
    } else markRef.current.setLatLng([lat, lng]);
    const r = parseInt(form.radius_m) || 150;
    if (!circRef.current) circRef.current = L.circle([lat, lng], { radius: r, color: "#C4738A", weight: 1.5, fillColor: "#C4738A", fillOpacity: 0.08 }).addTo(map);
    else { circRef.current.setLatLng([lat, lng]); circRef.current.setRadius(r); }
  }, [form.lat, form.lng, form.radius_m]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Browser tidak mendukung GPS");
    toast.info("Mengambil lokasi GPS...");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6), lng = pos.coords.longitude.toFixed(6);
        setForm((f) => ({ ...f, lat, lng }));
        if (mapObj.current) mapObj.current.setView([pos.coords.latitude, pos.coords.longitude], 17);
        toast.success("Koordinat terisi dari GPS");
      },
      () => toast.error("Gagal mengambil lokasi — izinkan akses lokasi di browser"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Nama lokasi wajib diisi (min. 2 karakter)");
    const payload = { name: form.name.trim(), lat: parseFloat(form.lat), lng: parseFloat(form.lng), radius_m: parseInt(form.radius_m) || 150 };
    if (isNaN(payload.lat) || isNaN(payload.lng)) return toast.error("Koordinat lat/lng wajib berupa angka");
    try {
      if (editing) { await api.put(`/admin/work-locations/${editing}`, payload); toast.success("Lokasi diperbarui"); }
      else { await api.post("/admin/work-locations", payload); toast.success("Lokasi kerja ditambahkan"); }
      setEditing(null); setForm(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan lokasi"); }
  };
  const del = async (l) => {
    if (!window.confirm(`Hapus lokasi "${l.name}"?`)) return;
    try { await api.delete(`/admin/work-locations/${l.id}`); toast.success("Lokasi dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input data-testid="loc-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="Nama lokasi (mis. Toko Cirebon)" className={inp} />
          <input data-testid="loc-lat" value={form.lat} onChange={(e) => setF("lat", e.target.value)} placeholder="Latitude, mis. -6.7320" className={inp} />
          <input data-testid="loc-lng" value={form.lng} onChange={(e) => setF("lng", e.target.value)} placeholder="Longitude, mis. 108.5523" className={inp} />
          <div className="flex items-center gap-1">
            <input data-testid="loc-radius" type="number" min="20" max="5000" value={form.radius_m} onChange={(e) => setF("radius_m", e.target.value)} className={inp} />
            <span className="text-[11px] text-slate-400 flex-shrink-0">m</span>
          </div>
          <button data-testid="loc-save" onClick={save} className="h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors">
            {editing ? <><Check className="w-3.5 h-3.5" /> Simpan</> : <><Plus className="w-3.5 h-3.5" /> Tambah</>}
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div data-testid="loc-map" ref={mapRef} className="h-56 flex-1 rounded-xl overflow-hidden border border-slate-100 z-0" />
          <button data-testid="loc-gps" type="button" onClick={useMyLocation}
            className="h-9 sm:h-auto px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
            <LocateFixed className="w-3.5 h-3.5" /> Pakai Lokasi Saya Sekarang
          </button>
        </div>
        <p className="text-[10px] text-slate-400">Klik peta atau geser pin untuk menentukan titik absen; lingkaran = radius area. Tips: buka Google Maps → klik kanan titik lokasi → salin koordinat. {editing && <button onClick={() => { setEditing(null); setForm(blank); }} className="text-[#C4738A] font-semibold ml-1">Batal ubah</button>}</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Lokasi Kerja</th>
              <th className="text-left px-3 py-2.5 font-semibold">Koordinat</th>
              <th className="text-center px-3 py-2.5 font-semibold w-24">Radius</th>
              <th className="px-3 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={4} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {(items || []).map((l) => (
              <tr key={l.id} data-testid={`loc-row-${l.id}`}>
                <td className="px-4 py-2.5"><span className="text-xs font-semibold text-slate-800 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#C4738A]" />{l.name}</span></td>
                <td className="px-3 py-2.5 text-[11px] font-mono text-slate-500">{l.lat}, {l.lng}</td>
                <td className="px-3 py-2.5 text-center text-[11px] font-bold text-slate-500">{l.radius_m} m</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`loc-edit-${l.id}`} onClick={() => startEditBtn(l)} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`loc-del-${l.id}`} onClick={() => del(l)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items && !items.length && <tr><td colSpan={4} className="px-4 py-12 text-center text-xs text-slate-400">Belum ada lokasi kerja — tambahkan toko/gudang agar badge "Dalam Area" aktif.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  function startEditBtn(l) { setEditing(l.id); setForm({ name: l.name, lat: String(l.lat), lng: String(l.lng), radius_m: l.radius_m }); }
}

function PayrollRulesTab() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/admin/payroll-settings").then((r) => setForm(r.data)).catch(() => setForm(false)); }, []);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      await api.put("/admin/payroll-settings", {
        meal_allowance_per_day: parseInt(form.meal_allowance_per_day) || 0,
        overtime_rate_per_hour: parseInt(form.overtime_rate_per_hour) || 0,
        overtime_min_minutes: parseInt(form.overtime_min_minutes) || 0,
        alfa_deduction: parseInt(form.alfa_deduction) || 0,
        payday: Math.min(31, Math.max(1, parseInt(form.payday) || 27)),
      });
      toast.success("Aturan payroll disimpan — dipakai saat Buat Payroll berikutnya");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan aturan"); }
  };
  if (form === null) return <div className="py-12 text-center text-xs text-slate-400">Memuat...</div>;
  if (form === false) return <div className="py-12 text-center text-xs text-slate-400">Halaman ini khusus admin/HR.</div>;
  const fields = [
    ["payday", "Tanggal Gajian (1-31)", "Cut-off: gajian tgl 27 → periode absensi dihitung tgl 26 bulan lalu s/d 26 bulan ini (H-1 gajian). Otomatis menyesuaikan jumlah hari tiap bulan (28/29/30/31)."],
    ["overtime_rate_per_hour", "Tarif Lembur per Jam (Rp)", "Dihitung dari jam pulang yang melebihi jam shift di absensi."],
    ["overtime_min_minutes", "Minimal Lembur (menit)", "Kelebihan jam di bawah ambang ini tidak dihitung lembur."],
  ];
  return (
    <div data-testid="payroll-rules" className="bg-white rounded-2xl border border-slate-100 p-5 max-w-xl space-y-4">
      <div>
        <h3 className="font-display font-bold text-sm text-slate-900">Aturan Perhitungan Otomatis</h3>
        <p className="text-[11px] text-slate-400">Dipakai tombol "Buat Payroll" untuk menghitung Lembur dari data absensi periode berjalan. Potongan alfa dihitung otomatis: gaji pokok ÷ hari kerja aktual periode × jumlah hari alfa. Tunjangan & potongan tetap (BPJS dll) diatur di menu Remunisasi.</p>
      </div>
      {fields.map(([k, label, hint]) => (
        <div key={k}>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">{label}</label>
          <input data-testid={`rule-${k}`} type="number" min="0" value={form[k]} onChange={(e) => setF(k, e.target.value)} className={`${inp} w-full`} />
          <p className="text-[10px] text-slate-400 mt-1">{hint}</p>
        </div>
      ))}
      <button data-testid="rule-save" onClick={save} className="h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"><Check className="w-3.5 h-3.5" /> Simpan Aturan</button>
    </div>
  );
}
